import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/apiClient';

const AuthContext = createContext();

// Demo users fallback for MVP when API is unavailable
const DEMO_USERS = {
  'demo@test.com': { password: 'demo123', role: 'user', balance: 25 },
  'admin@test.com': { password: 'admin123', role: 'admin', balance: 100 }
};

const buildDemoUser = (email) => {
  const demo = DEMO_USERS[email] || { role: 'user', balance: 10 };
  return { id: 1, email, role: demo.role, balance: demo.balance };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up axios defaults - handled by apiClient
  // No need to manually set headers here

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      // Optimistically hydrate from localStorage for MVP/demo
      if (token && savedUser) {
        try { setUser(JSON.parse(savedUser)); } catch (_) {}
      }

      if (token) {
        try {
          const response = await authAPI.getProfile();
          const profileUser = response && response.data && response.data.user;
          if (profileUser && typeof profileUser === 'object') {
            setUser(profileUser);
            localStorage.setItem('user', JSON.stringify(profileUser));
          } else {
            // Invalid profile shape (possibly HTML). Keep local demo user if present.
            console.warn('Invalid profile response, keeping local user.');
          }
        } catch (error) {
          console.error('Auth check failed:', error?.message);
          // Keep demo/local auth when API is down, otherwise clear
          if (!savedUser) {
            localStorage.removeItem('token');
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const data = response && response.data;
      const token = data && data.token;
      const apiUser = data && data.user;
      if (!token || !apiUser) {
        throw new Error('Invalid login response');
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(apiUser));
      setUser(apiUser);
      
      // If this is a demo login, don't try to check profile
      if (data.isDemo) {
        return { success: true, isDemo: true };
      }
      
      return { success: true };
    } catch (error) {
      // MVP fallback: allow demo users locally if API fails or response invalid
      const demo = DEMO_USERS[email];
      if (demo && demo.password === password) {
        const demoToken = `demo-token-${email.split('@')[0]}`;
        const demoUser = buildDemoUser(email);
        localStorage.setItem('token', demoToken);
        localStorage.setItem('user', JSON.stringify(demoUser));
        setUser(demoUser);
        setError(null);
        return { success: true, isDemo: true };
      }
      const message = (error && error.response && error.response.data && error.response.data.error) || error?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.register({ email, password });
      const data = response && response.data;
      const token = data && data.token;
      const apiUser = data && data.user;
      if (!token || !apiUser) {
        throw new Error('Invalid register response');
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(apiUser));
      setUser(apiUser);
      return { success: true };
    } catch (error) {
      // MVP fallback: create a local demo user if API is down
      const demoToken = `demo-token-${email.split('@')[0]}`;
      const demoUser = buildDemoUser(email);
      localStorage.setItem('token', demoToken);
      localStorage.setItem('user', JSON.stringify(demoUser));
      setUser(demoUser);
      setError(null);
      return { success: true };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  const updateUserBalance = (newBalance) => {
    if (user) {
      const updated = { ...user, balance: newBalance };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUserBalance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
